export interface PushResult { success: boolean; error?: string; }

export interface PushApi {
  tellAgent(message: string): Promise<PushResult>;
  informAgent(message: string): Promise<PushResult>;
  askAgent(message: string): Promise<PushResult>;
  agentSay(message: string, options?: { userInput: string }): Promise<PushResult>;
  transferAgent(name: string, opts?: { preserveHistory?: boolean }): Promise<PushResult>;
}

/**
 * A Room outlives its transport: `room.ts` clears its reference only in its
 * own `disconnect()`, so after a server-side end `getRoom()` keeps returning a
 * corpse. Without this, pushes poll out the whole budget before failing.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isLive(room: any): boolean {
  return !!room && room.state !== 'disconnected';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findAgent(room: any): any | null {
  for (const p of room.remoteParticipants.values()) {
    const attrs = p.attributes ?? {};
    if (attrs['lk.agent.state']) return p;
  }
  return null;
}

/**
 * How long a push waits for the agent worker to show up.
 *
 * The room connecting and the agent JOINING it are seconds apart — the worker
 * builds STT/LLM/TTS/VAD first. Pushes in that window used to fail instantly
 * with 'no agent connected' and return a result nobody awaited. This was the
 * chat input's retry budget; it lives here so every push method gets it.
 *
 * Scope: covers "room connected, agent not here yet" only. A push made before
 * any room exists still fails fast — a bridge that wants to push as early as
 * possible should wait for the `agentReady` event (`sessionStart` is too
 * early; the agent is still starting up).
 */
const AGENT_WAIT_ATTEMPTS = 14;
const AGENT_WAIT_INTERVAL_MS = 1000;

export interface PushApiOptions {
  /** Test-only: production passes neither, and would otherwise spend 14 real
   * seconds proving the give-up path. */
  agentWaitAttempts?: number;
  agentWaitIntervalMs?: number;
}

async function perform(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  room: any,
  method: string,
  payload: object,
  attempts: number,
  intervalMs: number,
): Promise<PushResult> {
  let agent = findAgent(room);
  for (let i = 0; !agent && i < attempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    // The session can end inside a ~14s wait (close button, idle timeout).
    // `roomManager` always disconnects a room before replacing it, so a room
    // that stopped being live is the one signal needed here: it covers both
    // teardown and the disconnect-then-reconnect swap.
    if (!isLive(room)) return { success: false, error: 'not connected' };
    agent = findAgent(room);
  }
  if (!agent) return { success: false, error: 'no agent connected' };
  try {
    const raw = await room.localParticipant.performRpc({
      destinationIdentity: agent.identity,
      method,
      payload: JSON.stringify(payload),
    });
    try {
      const parsed = JSON.parse(raw);
      // The session handlers answer `{success}`; the workflow's
      // `setActiveAgent` answers `{ok}`. Either one saying no is a refusal.
      return parsed.success === false || parsed.ok === false
        ? { success: false, error: parsed.error ?? 'agent error' }
        : { success: true };
    } catch {
      return { success: true };
    }
  } catch (e) {
    return { success: false, error: String((e as Error).message ?? e) };
  }
}

export function createPushApi(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRoom: () => any | null,
  options: PushApiOptions = {},
): PushApi {
  const attempts = options.agentWaitAttempts ?? AGENT_WAIT_ATTEMPTS;
  const intervalMs = options.agentWaitIntervalMs ?? AGENT_WAIT_INTERVAL_MS;

  function push(method: string, payload: object): Promise<PushResult> {
    const room = getRoom();
    if (!isLive(room)) return Promise.resolve({ success: false, error: 'not connected' });
    return perform(room, method, payload, attempts, intervalMs);
  }

  return {
    tellAgent: (m) => push('tellAgent', { message: m }),
    informAgent: (m) => push('informAgent', { message: m }),
    askAgent: (m) => push('askAgent', { message: m }),
    agentSay: (m, options) => push('agentSay', { message: m, ...(options ? { userInput: options.userInput } : {}) }),
    // The agent registers this handoff as `setActiveAgent` (no
    // `transferAgent` alias, unlike the four above) — see agent/main.py.
    transferAgent: (name, opts) =>
      push('setActiveAgent', { name, preserveHistory: opts?.preserveHistory ?? true }),
  };
}
